/**
 * Extract tracking UUID or order number from scanned QR text.
 * Supports customer portal URLs, custom domains, hash routes, and bare IDs.
 */
export function parseTrackingFromQrPayload(text) {
  if (!text) return null;
  const raw = String(text).trim();
  if (!raw) return null;

  const uuidRe = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
  const orderNumRe = /SHP-\d{8}-\d{3,}/i;

  const pathMatch = raw.match(/\/track\/([^/?&#\s]+)/i);
  if (pathMatch) {
    const seg = decodeURIComponent(pathMatch[1]);
    if (uuidRe.test(seg)) return { trackingId: seg.match(uuidRe)[0], orderNumber: null };
    if (orderNumRe.test(seg)) return { trackingId: null, orderNumber: seg.match(orderNumRe)[0] };
    return { trackingId: seg, orderNumber: null };
  }

  const hashMatch = raw.match(/#(?:tracking|track)\/([^/?&#\s]+)/i);
  if (hashMatch) {
    const seg = decodeURIComponent(hashMatch[1]);
    if (uuidRe.test(seg)) return { trackingId: seg.match(uuidRe)[0], orderNumber: null };
    if (orderNumRe.test(seg)) return { trackingId: null, orderNumber: seg.match(orderNumRe)[0] };
    return { trackingId: seg, orderNumber: null };
  }

  try {
    const url = new URL(raw.includes('://') ? raw : `https://x${raw.startsWith('/') ? '' : '/'}${raw}`);
    for (const key of ['tracking', 'tracking_id', 't', 'id', 'order', 'order_number']) {
      const v = url.searchParams.get(key);
      if (v) {
        if (uuidRe.test(v)) return { trackingId: v.match(uuidRe)[0], orderNumber: null };
        if (orderNumRe.test(v)) return { trackingId: null, orderNumber: v.match(orderNumRe)[0] };
        return { trackingId: v, orderNumber: null };
      }
    }
  } catch {
    // not a URL
  }

  if (uuidRe.test(raw)) return { trackingId: raw.match(uuidRe)[0], orderNumber: null };
  if (orderNumRe.test(raw)) return { trackingId: null, orderNumber: raw.match(orderNumRe)[0] };

  if (/^[a-zA-Z0-9_-]{8,}$/.test(raw)) {
    return { trackingId: raw, orderNumber: null };
  }

  return null;
}

export function findOrderByQrPayload(orders, decodedText) {
  const parsed = parseTrackingFromQrPayload(decodedText);
  if (!parsed) return null;
  const { trackingId, orderNumber } = parsed;
  return orders.find((o) => {
    if (trackingId && o.tracking_id === trackingId) return true;
    if (orderNumber && o.order_number === orderNumber) return true;
    if (trackingId && o.order_number === trackingId) return true;
    if (orderNumber && o.tracking_id === orderNumber) return true;
    return false;
  }) || null;
}